/**
 * The classify behavior in the browser — one item, or a batch over a scope.
 *
 * SPEC-custom-agent-template.md §4: one runner, two hosts. This is the browser
 * host (manual runs: one item from the item page or list, "Run now" over a
 * scope); the server host is supabase/functions/ambient/lib/classifyServer.ts
 * (events, schedules, tasks). Both build the prompt, parse the reply and apply
 * the behavior's switches with the shared core
 * (supabase/functions/_shared/classifyCore.ts), and both write the proposal
 * through propose_item_classification — the same row either way.
 *
 * Every run: the agent must be on and the plan must include AI; each model
 * call counts against the agent's monthly cap; nothing is written the
 * definition does not declare; every outcome writes a decision record.
 */

import { getSupabaseClient } from './supabaseClient';
import { getAgentSettings, type AgentSettings } from './agentSettingsService';
import {
  AIR_TRAFFIC_CONTROLLER,
  AIR_TRAFFIC_CONTROLLER_AGENT_TYPE,
  agentReportHref,
  assertAgentMayProduce,
} from './ai/agentDefinitions';
import {
  CONFIDENCE_SCORE,
  meetsConfidence,
  resolveClassifyConfigFrom,
  type ClassifyBehaviorConfig,
} from '../../supabase/functions/_shared/classifyCore.ts';
import { runAtcAgent } from './llmAirTrafficControllerService';
import { createAgentProposal } from './itemClassificationService';
import { countAgentModelCall, enqueueAgentDelivery } from './agentRuntimeService';
import { isWorkspaceEntitledToAi } from './ai/aiAssistSettings';
import { fetchItemsForScope } from './agentScopeItems';
import type { BatchStats, RunScope } from './agentRunService';
import { currentAgentRunId } from './agentRunService';
import type { ItemClassificationProposal } from '../types/prioritization/itemClassification';

export interface ClassifyAgent {
  agentType: string;
  name: string;
}

export const AIR_TRAFFIC_CONTROLLER_IDENTITY: ClassifyAgent = {
  agentType: AIR_TRAFFIC_CONTROLLER_AGENT_TYPE,
  name: AIR_TRAFFIC_CONTROLLER.name,
};

export const CLASSIFY_STACK_VERSION = 'classify/2';

export function resolveClassifyConfig(settings: AgentSettings | null): ClassifyBehaviorConfig {
  return resolveClassifyConfigFrom(settings?.behaviorConfig);
}

/** Only unclassified items, unless the agent may re-propose on classified ones. */
const UNCLASSIFIED = 'classification_status.eq.empty,classification_status.is.null';

// ── Decision record ───────────────────────────────────────────────────────────

async function record(input: {
  agent: ClassifyAgent;
  workspaceId: string;
  itemId: string;
  outputKind: 'proposal:classification' | 'abstain' | 'skipped' | 'error';
  rationale: string;
  outputId?: string | null;
  proposedValue?: unknown;
  confidence?: number | null;
  model?: string | null;
  runId?: string | null;
  runByUserId?: string | null;
}): Promise<void> {
  // The one write path (migration 824): the function checks membership and fixes the identity.
  const { error } = await getSupabaseClient().rpc('record_agent_decision', {
    p_workspace_id: input.workspaceId,
    p_agent_type: input.agent.agentType,
    p_object_id: input.itemId,
    p_output_kind: input.outputKind,
    p_rationale: input.rationale,
    p_object_type: 'item',
    p_field: 'classification',
    p_output_id: input.outputId ?? null,
    p_proposed_value: input.proposedValue ?? null,
    p_confidence: input.confidence ?? null,
    p_evidence: [],
    p_stack_version: CLASSIFY_STACK_VERSION,
    p_model: input.model ?? null,
    p_run_id: input.runId ?? null,
    p_sponsor_user_id: null,
    p_run_by_user_id: input.runByUserId ?? null,
    p_task_id: null,
  });
  if (error) console.warn('[classify] decision record not written:', error.message);
}

// ── One item ──────────────────────────────────────────────────────────────────

export type ClassifyOutcome =
  | { outcome: 'proposed'; proposal: ItemClassificationProposal | null }
  | { outcome: 'abstained'; reason: string }
  | { outcome: 'skipped'; reason: string }
  | { outcome: 'error'; reason: string };

export interface ClassifyParams {
  /** The agent running the behavior; the Air Traffic Controller when absent. */
  agent?: ClassifyAgent;
  workspaceId: string;
  itemId: string;
  /** Pass the item's text when the caller has it; else it is read. */
  title?: string;
  description?: string | null;
  /** The agent_run_logs row for a batch; null for a single manual run. */
  runId?: string | null;
  runByUserId?: string | null;
  /** Pass settings when the caller already has them (batch); else they are read. */
  settings?: AgentSettings | null;
  /** Announce the proposal to the agent's audience. A batch announces once, at the end. */
  deliver?: boolean;
}

export async function classifyItem(params: ClassifyParams): Promise<ClassifyOutcome> {
  const agent = params.agent ?? AIR_TRAFFIC_CONTROLLER_IDENTITY;
  if (!params.workspaceId) return { outcome: 'skipped', reason: 'No workspace is selected.' };
  const settings =
    params.settings !== undefined ? params.settings : (await getAgentSettings(params.workspaceId, agent.agentType)).data;
  if (!settings?.enabled) return { outcome: 'skipped', reason: `${agent.name} is off for this workspace.` };
  if (!isWorkspaceEntitledToAi(params.workspaceId)) return { outcome: 'skipped', reason: 'This workspace\'s plan does not include AI features.' };

  let title = params.title;
  let description = params.description ?? null;
  if (title === undefined) {
    const { data } = await getSupabaseClient().from('items').select('title, description').eq('id', params.itemId).maybeSingle();
    if (!data) return { outcome: 'skipped', reason: 'Item not found.' };
    title = (data as { title: string | null }).title ?? '';
    description = (data as { description: string | null }).description ?? null;
  }
  const base = { agent, workspaceId: params.workspaceId, itemId: params.itemId, runId: params.runId, runByUserId: params.runByUserId };
  if (!title.trim()) {
    const reason = 'Cannot classify an item with no title.';
    await record({ ...base, outputKind: 'abstain', rationale: reason });
    return { outcome: 'abstained', reason };
  }

  // Every model call counts against the agent's monthly cap (spec §2 K).
  const meter = await countAgentModelCall(params.workspaceId, agent.agentType);
  if (!meter.allowed) {
    const reason = meter.error ?? `${agent.name} has used its ${meter.limit} model calls for this month. An administrator can raise the cap on its settings page.`;
    await record({ ...base, outputKind: 'error', rationale: reason });
    return { outcome: 'error', reason };
  }

  const sources = { reference_docs: true, ...(settings.evidenceSources ?? {}) };
  const referenceDocs = sources.reference_docs
    ? (settings.referenceDocs ?? []).filter((d) => d && typeof d.content === 'string' && d.content.trim())
    : [];
  const limits = (settings.limits ?? {}) as { timeoutMs?: number };

  let reply: Awaited<ReturnType<typeof runAtcAgent>>;
  try {
    reply = await runAtcAgent({
      title,
      description,
      referenceDocs,
      model: settings.model,
      systemPrompt: settings.systemPrompt ?? null,
      modelParams: settings.modelParams ?? null,
      timeoutMs: limits.timeoutMs,
    });
  } catch (err) {
    const reason = err instanceof Error ? err.message : 'The model call failed.';
    await record({ ...base, outputKind: 'error', rationale: reason });
    return { outcome: 'error', reason };
  }
  const { result, model } = reply;
  const confidence = CONFIDENCE_SCORE[result.confidence];
  const config = resolveClassifyConfig(settings);
  const proposed = { category: result.suggestedCategory, altitude_level: result.altitudeLevel, execution_profile: result.executionProfile };
  if (!meetsConfidence(result.confidence, config.minConfidence)) {
    const reason = `Confidence ${result.confidence} is below this agent's floor (${config.minConfidence}). ${result.rationale}`;
    await record({ ...base, outputKind: 'abstain', rationale: reason, proposedValue: proposed, confidence, model });
    return { outcome: 'abstained', reason };
  }

  assertAgentMayProduce(AIR_TRAFFIC_CONTROLLER, 'proposal:classification');
  const saved = await createAgentProposal({
    workspaceId: params.workspaceId,
    itemId: params.itemId,
    agentName: agent.agentType,
    classificationFields: {
      category: result.suggestedCategory,
      altitudeLevel: result.altitudeLevel,
      executionProfile: result.executionProfile,
      questionResponses: result.questionResponses,
      rationale: result.rationale,
      confidence: result.confidence,
    },
  });
  if (saved.error || !saved.data) {
    const reason = saved.error ?? 'The proposal could not be saved.';
    await record({ ...base, outputKind: 'error', rationale: reason, proposedValue: proposed, confidence, model });
    return { outcome: 'error', reason };
  }
  await record({ ...base, outputKind: 'proposal:classification', outputId: saved.data.id, rationale: result.rationale, proposedValue: proposed, confidence, model });

  if (params.deliver !== false && ((settings.delivery ?? {}) as { notifyRoles?: string[] }).notifyRoles?.length) {
    await enqueueAgentDelivery({
      workspaceId: params.workspaceId,
      agentType: agent.agentType,
      title: `${agent.name} proposed ${result.suggestedCategory}, altitude ${result.altitudeLevel}, for "${title}"`,
      body: result.rationale,
      link: `/prioritization/items/${params.itemId}`,
      itemIds: [params.itemId],
      runId: params.runId ?? null,
      confidence,
    });
  }
  return { outcome: 'proposed', proposal: saved.data };
}

// ── Batch over a scope ────────────────────────────────────────────────────────


/**
 * Run over a scope. Matches AgentBatchRunner so the Agents hub and the run
 * panel can drive it. Items with an open proposal are skipped (the idempotency
 * key); classified items are skipped unless the agent may re-propose.
 */
export async function runClassifyBatch(
  workspaceId: string,
  scope: RunScope,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
  runByUserId?: string | null,
  agent: ClassifyAgent = AIR_TRAFFIC_CONTROLLER_IDENTITY,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };
  const { data: settings } = await getAgentSettings(workspaceId, agent.agentType);
  if (!settings?.enabled) return stats;
  const config = resolveClassifyConfig(settings);
  const limits = (settings.limits ?? {}) as { perRunObjectCap?: number; batchSize?: number; failurePosture?: string };
  const cap = Number(limits.perRunObjectCap ?? AIR_TRAFFIC_CONTROLLER.limits.perRunObjectCap);

  const { items, error } = await fetchItemsForScope(workspaceId, scope, {
    agentType: agent.agentType,
    exitStatuses: ['active'],
    limit: cap,
    ...(config.includeClassified ? {} : { emptyFilter: UNCLASSIFIED }),
  });
  if (error) {
    console.warn('[classify] scope error:', error);
    stats.errors += 1;
    return stats;
  }
  stats.itemsRead = items.length;

  // Open proposals settle the fact; so does a classification, unless the agent may re-propose.
  const ids = items.map((i) => i.id);
  const settled = new Set<string>();
  if (ids.length) {
    const supabase = getSupabaseClient();
    const { data: open } = await supabase.from('item_classification_proposals').select('item_id').in('item_id', ids).eq('status', 'active');
    for (const r of (open ?? []) as Array<{ item_id: string }>) settled.add(r.item_id);
    if (!config.includeClassified) {
      const { data: done } = await supabase.from('items').select('id').in('id', ids).eq('classification_status', 'approved');
      for (const r of (done ?? []) as Array<{ id: string }>) settled.add(r.id);
    }
  }

  const runId = await currentAgentRunId(workspaceId, agent.agentType);
  const total = items.length;
  let done = 0;
  const proposedItems: string[] = [];
  onProgress(0, total);

  const batchSize = Math.max(1, Math.min(25, Number(limits.batchSize ?? AIR_TRAFFIC_CONTROLLER.limits.batchSize)));
  const failStop = limits.failurePosture === 'fail_run';
  for (let i = 0; i < items.length; i += batchSize) {
    if (signal.aborted) return stats;
    const chunk = items.slice(i, i + batchSize);
    const outcomes = await Promise.all(chunk.map((item) =>
      settled.has(item.id)
        ? Promise.resolve<ClassifyOutcome>({ outcome: 'skipped', reason: 'Already proposed or classified.' })
        : classifyItem({ agent, workspaceId, itemId: item.id, title: item.title, description: item.description, runId, runByUserId, settings, deliver: false })));
    let failed = false;
    outcomes.forEach((outcome, j) => {
      if (outcome.outcome === 'proposed') { stats.processed += 1; stats.proposed += 1; proposedItems.push(chunk[j].id); }
      else if (outcome.outcome === 'abstained') { stats.processed += 1; }
      else if (outcome.outcome === 'skipped') { stats.skipped += 1; }
      else { stats.errors += 1; failed = true; }
    });
    done += chunk.length;
    onProgress(done, total);
    if (failed && failStop) return stats;
  }

  // One announcement per run, to the agent's audience, by its digest setting (spec §2 F).
  if (proposedItems.length > 0) {
    await enqueueAgentDelivery({
      workspaceId,
      agentType: agent.agentType,
      title: `${agent.name} proposed a classification for ${proposedItems.length} item${proposedItems.length === 1 ? '' : 's'}`,
      body: `${stats.proposed} proposal${stats.proposed === 1 ? '' : 's'} are waiting for review.`,
      link: agentReportHref(agent.agentType),
      itemIds: proposedItems,
      runId,
    });
  }
  return stats;
}
